import * as React from 'react';
import { createPortal } from 'react-dom';

import { BookmarkOnMobileDeviceModalProvider } from '@/components/BookmarkOnMobileDeviceModal';
import { usePermissions } from '@/hooks/usePermissions';
import { useWebflowPoc } from '@/hooks/useWebflowPoc';
import { useWebflowUtilitiesSlotAnchor } from '@/lib/webflow-poc/useWebflowUtilitiesSlotAnchor';
import { WebflowUtilitiesOverlay } from '@/lib/webflow-poc/WebflowUtilitiesOverlay';
import { tokenStorage } from '@/modules/accounts/tokenStorage';

type WebflowStaticNavAccountPortalProps = {
  containerRef: React.RefObject<HTMLElement | null>;
  htmlKey: string | null | undefined;
};

/**
 * TW utility cluster for static Webflow pages.
 * Anchors to the configured Webflow mount element (id or class); renders outside
 * Webflow CSS isolation so AccountMenu / icons keep Chakra styles.
 */
export const WebflowStaticNavAccountPortal: React.FC<
  WebflowStaticNavAccountPortalProps
> = ({ containerRef, htmlKey }) => {
  const permissions = usePermissions();
  const { isEnabled, utilitiesMount } = useWebflowPoc();

  const shouldShow =
    isEnabled &&
    !permissions.isLoading &&
    permissions.isAuthenticated &&
    !tokenStorage.getItem('isMobile');

  const anchor = useWebflowUtilitiesSlotAnchor({
    containerRef,
    htmlKey,
    enabled: shouldShow,
    utilitiesMount,
  });

  if (!shouldShow || typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    <BookmarkOnMobileDeviceModalProvider>
      <WebflowUtilitiesOverlay
        anchor={anchor}
        dataAttribute="data-tradewing-static-account-overlay"
        lineHeight="normal"
      />
    </BookmarkOnMobileDeviceModalProvider>,
    document.body
  );
};
